import { useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from 'react';
import type { ItemSpec } from '../../../engine';
import { t } from '../../../i18n';
import { itemName } from '../../../app/describe';
import { swallowReleaseClick } from '../../useDragGesture';
import { NightObject, type ObjectSpec } from './Objects';
import { mantelObject } from './Item';

/**
 * The Mantel (docs/LONG_NIGHT_ART_DIRECTION.md §12.2): shallow niches in a row, counted left to
 * right. An empty niche is drawn as a faint outline: emptiness is visible (Empty Chair pays for
 * it). Dragging lifts an item, which tilts toward the pointer while the others slide apart; with
 * a keyboard, focus an item and move it with the arrow keys. At the table a small arrow under the
 * leftmost niche shows where the count starts.
 */

const SIZES = { counter: 40, sheet: 50, table: 20, glass: 16 } as const;
/** Gap between niches, px. */
const GAP = { counter: 6, sheet: 8, table: 3, glass: 3 } as const;

export function Mantel({
  items,
  slots,
  size = 'counter',
  objectOf = (item) => mantelObject(item),
  onOpen,
  onReorder,
  countStart = false,
  label,
}: {
  items: readonly ItemSpec[];
  slots: number;
  size?: keyof typeof SIZES;
  objectOf?: (item: ItemSpec) => ObjectSpec;
  onOpen?: (item: ItemSpec) => void;
  onReorder?: (uids: string[]) => void;
  /** Shows where the Tally starts (at the table). */
  countStart?: boolean;
  label?: string;
}) {
  const w = SIZES[size];
  const step = w + GAP[size];
  const [drag, setDrag] = useState<{ uid: string; dx: number; tilt: number } | null>(null);
  const press = useRef<{ uid: string; x: number; lastX: number; started: boolean } | null>(null);
  const from = drag ? items.findIndex((i) => i.uid === drag.uid) : -1;
  const to =
    drag && from >= 0
      ? Math.max(0, Math.min(items.length - 1, from + Math.round(drag.dx / step)))
      : -1;

  const down = (item: ItemSpec, e: PointerEvent<HTMLButtonElement>) => {
    if (!onReorder || e.button > 0) return;
    press.current = { uid: item.uid, x: e.clientX, lastX: e.clientX, started: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const move = (e: PointerEvent<HTMLButtonElement>) => {
    const p = press.current;
    if (!p) return;
    const dx = e.clientX - p.x;
    if (!p.started && Math.abs(dx) < 6) return;
    p.started = true;
    // Tilts toward where it is going, like a card in hand.
    const tilt = Math.max(-10, Math.min(10, (e.clientX - p.lastX) * 1.6));
    p.lastX = e.clientX;
    setDrag({ uid: p.uid, dx, tilt });
  };
  const up = (e: PointerEvent<HTMLButtonElement>) => {
    const p = press.current;
    press.current = null;
    if (!p?.started) return;
    swallowReleaseClick(e.clientX, e.clientY);
    setDrag(null);
    if (from >= 0 && to >= 0 && to !== from) onReorder?.(moved(items, from, to));
  };
  const key = (item: ItemSpec, i: number, e: KeyboardEvent<HTMLButtonElement>) => {
    if (!onReorder) return;
    const d = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0;
    if (!d || i + d < 0 || i + d >= items.length) return;
    e.preventDefault();
    onReorder(moved(items, i, i + d));
    // Keep the focus on the item that moved.
    const list = e.currentTarget.closest('ol');
    requestAnimationFrame(() =>
      list?.querySelector<HTMLElement>(`[data-tally-uid="${CSS.escape(item.uid)}"]`)?.focus(),
    );
  };

  return (
    <ol
      className={`mn mn-${size} ${drag ? 'is-dragging' : ''}`}
      style={{ '--niche': `${w}px`, '--gap': `${GAP[size]}px` } as CSSProperties}
      aria-label={label ?? t('night.mantel')}
    >
      {Array.from({ length: Math.max(slots, items.length) }, (_, i) => {
        const item = items[i];
        if (!item)
          return (
            <li key={`empty-${i}`} className="mn-niche is-empty">
              <span className="mn-hollow" aria-label={t('night.emptySlot')} />
            </li>
          );
        const dragging = drag?.uid === item.uid;
        // While one is dragged, the ones between where it was and where it is going slide apart.
        const shift =
          drag && !dragging && from >= 0
            ? from < to && i > from && i <= to
              ? -step
              : from > to && i < from && i >= to
                ? step
                : 0
            : 0;
        const style = dragging
          ? {
              transform: `translateX(${drag.dx}px) translateY(-6px) rotate(${drag.tilt}deg) scale(1.08)`,
            }
          : shift
            ? { transform: `translateX(${shift}px)` }
            : undefined;
        return (
          <li key={item.uid} className={`mn-niche ${dragging ? 'is-lifted' : ''}`} style={style}>
            <button
              type="button"
              className="mn-item"
              data-tally-uid={item.uid}
              aria-label={`${i + 1}. ${itemName(item.id)}`}
              title={itemName(item.id)}
              onClick={() => onOpen?.(item)}
              onPointerDown={(e) => down(item, e)}
              onPointerMove={move}
              onPointerUp={up}
              onPointerCancel={() => {
                press.current = null;
                setDrag(null);
              }}
              onKeyDown={(e) => key(item, i, e)}
            >
              <NightObject spec={objectOf(item)} size={w} />
            </button>
          </li>
        );
      })}
      {countStart && <li className="mn-start" aria-hidden="true" />}
    </ol>
  );
}

function moved(items: readonly ItemSpec[], from: number, to: number): string[] {
  const uids = items.map((i) => i.uid);
  const [x] = uids.splice(from, 1);
  uids.splice(to, 0, x!);
  return uids;
}
